/**
 * CollectionDetailsDialog
 *
 * Admin peek inside a (potentially dead) saved presentation. Shows the
 * collection's slides as a thumbnail grid; clicking a slide opens the
 * standard SlidePreviewModal. Read-only — no admin destructive actions
 * surface here yet.
 */

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"

import type { SlideSenseSlide } from "@/features/slide-sense/types"

import { Inbox, Loader2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { fetchAdminCollection } from "@/features/slide-sense/admin-api"

import { SlideThumbnailImage } from "../SlideThumbnailImage"

export interface CollectionDetailsDialogProps {
  collectionId: string | null
  open: boolean
  onClose: () => void
  onPreviewSlide?: (slide: SlideSenseSlide, slides: SlideSenseSlide[]) => void
}

export function CollectionDetailsDialog({
  collectionId,
  open,
  onClose,
  onPreviewSlide,
}: CollectionDetailsDialogProps) {
  const { t } = useTranslation()

  const { data, isLoading, isError } = useQuery({
    queryKey: ["slide-sense", "admin", "collection", collectionId],
    queryFn: () => fetchAdminCollection(collectionId as string),
    enabled: open && Boolean(collectionId),
  })

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{data?.name ?? t("slideSense.usage.unused.collectionLoading")}</DialogTitle>
          <DialogDescription>
            {data
              ? t("slideSense.usage.unused.collectionMeta", {
                  count: data.slides.length,
                  owner: data.owner_name || data.owner_user_id || "—",
                })
              : t("slideSense.usage.unused.collectionLoading")}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
            </div>
          )}
          {isError && (
            <p className="text-destructive text-sm">{t("slideSense.usage.unused.error")}</p>
          )}
          {data && data.slides.length === 0 && (
            <div className="text-muted-foreground flex flex-col items-center justify-center gap-2 py-10 text-sm">
              <Inbox className="h-6 w-6" />
              <p>{t("slideSense.usage.unused.collectionEmpty")}</p>
            </div>
          )}
          {data && data.slides.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {data.slides.map((slide, idx) => (
                <button
                  key={slide.id}
                  className="group focus-visible:ring-ring border-border/45 bg-card/30 overflow-hidden rounded-md border text-left transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:outline-none"
                  type="button"
                  onClick={() => onPreviewSlide?.(slide, data.slides)}
                >
                  <div className="bg-muted/40 relative aspect-[16/9] w-full overflow-hidden">
                    <SlideThumbnailImage
                      alt={slide.ai_title}
                      className="h-full w-full object-contain"
                      slideId={slide.id}
                      thumbnailUrl={slide.thumbnail_url}
                    />
                    <Badge
                      className="absolute top-1.5 left-1.5 text-[10px] tabular-nums"
                      variant="secondary"
                    >
                      {idx + 1}
                    </Badge>
                  </div>
                  <div className="space-y-0.5 p-2">
                    <p className="truncate text-xs font-medium">
                      {slide.ai_title || `${t("slideSense.slides")} ${slide.slide_index + 1}`}
                    </p>
                    <p className="text-muted-foreground truncate text-[10px]">
                      {slide.source_filename}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
